// Profile customization: face, title, motto, "about me", accent color and a showcased item.
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useGame } from '../state.tsx';
import { Loading, Panel, ItemIcon, go, useLoad } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';
import { ACCENT_COLORS } from './Profile.tsx';

export function CustomizePage() {
  const { me, act, byId } = useGame();
  const { data: options } = useLoad(() => api.get('/api/profile/options'), []);
  const { data: profile } = useLoad(() => (me ? api.get(`/api/players/${me.id}`) : Promise.resolve(null)), [me?.id]);
  const { data: inv } = useLoad(() => api.get('/api/inventory'), []);
  const [form, setForm] = useState<any>(null);
  const [faces, setFaces] = useState<number[]>([]);
  const newFaces = () => setFaces(Array.from({ length: 11 }, () => Math.floor(Math.random() * 1_000_000)));
  useEffect(() => {
    if (profile && !form) setForm({ portrait: profile.portrait, title: profile.title, motto: profile.motto, about: profile.about, accent: profile.accent, showcaseItem: profile.showcaseItem });
  }, [profile]);
  useEffect(newFaces, []);
  if (!me || !options || !form || !inv) return <Loading />;
  const showable = inv.items.map((i: any) => byId.items.get(i.itemId)).filter((d: any) => d && ['weapon', 'armor', 'vehicle', 'collectible'].includes(d.category))
    .sort((a: any, b: any) => b.attack + b.defense - (a.attack + a.defense));
  const set = (k: string, v: unknown) => setForm({ ...form, [k]: v });
  const save = async () => {
    const r = await act('/api/profile/update', form, { success: () => ({ kind: 'good', text: 'Your profile has been updated.' }) });
    if (r) go(`profile/${me.id}`);
  };
  return (
    <div>
      <h1 className="page-title">Customize Your Profile</h1>
      <Panel title="Your face">
        <div className="row" style={{ flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {[profile.portrait, ...faces].map((seed: number, i: number) => (
            <a key={`${seed}-${i}`} onClick={() => set('portrait', seed)} title={i === 0 ? 'Current face' : 'Pick this face'}
              style={{ outline: form.portrait === seed ? `3px solid ${ACCENT_COLORS[form.accent]}` : 'none', display: 'inline-block' }}>
              <Portrait seed={seed} size={60} />
            </a>
          ))}
          <button className="btn small gray" onClick={newFaces}>More faces ↻</button>
        </div>
      </Panel>
      <div className="row">
        <div className="grow">
          <Panel title="Words">
            <div className="form-row">
              <label>Title (earned job ranks and achievements)</label>
              <select value={form.title} onChange={(e) => set('title', e.target.value)}>
                <option value="">(no title)</option>
                {options.titles.map((t: string) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>Motto (one line, 80 characters)</label>
              <input value={form.motto} maxLength={80} onChange={(e) => set('motto', e.target.value)} />
            </div>
            <div className="form-row">
              <label>About me (500 characters)</label>
              <textarea value={form.about} maxLength={500} rows={5} onChange={(e) => set('about', e.target.value)} />
              <div className="small dim right">{form.about.length}/500</div>
            </div>
          </Panel>
        </div>
        <div style={{ width: 300 }}>
          <Panel title="Accent color">
            <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
              {options.accents.map((a: string) => (
                <a key={a} onClick={() => set('accent', a)} title={a} className="swatch"
                  style={{ background: ACCENT_COLORS[a], outline: form.accent === a ? '3px solid var(--gold)' : 'none', outlineOffset: 2 }} />
              ))}
            </div>
          </Panel>
          <Panel title="Prized possession">
            <select value={form.showcaseItem} onChange={(e) => set('showcaseItem', e.target.value)} style={{ width: '100%' }}>
              <option value="">(nothing)</option>
              {showable.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {form.showcaseItem && <div style={{ marginTop: 6 }}><ItemIcon itemId={form.showcaseItem} size={64} /></div>}
          </Panel>
        </div>
      </div>
      <div className="right">
        <a className="small" onClick={() => go(`profile/${me.id}`)}>Cancel</a>{' '}
        <button className="btn big" onClick={save}>Save profile</button>
      </div>
    </div>
  );
}
